// src/pages/Article.jsx
import React, { useMemo, useRef, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Facebook,
  Instagram,
  ArrowRight,
  Star,
  Quote,
  ArrowDown,
  Clock,
  Calendar,
  Mail,
  Share2,
  Bookmark,
  Check,
} from "lucide-react";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { getArticleBySlug, getNextArticle } from "../data/articles/articles";

const SITE = {
  name: "D10 Pizza Fantasista",
  baseUrl: "https://www.d10s.pizza",
  logo: "/logo2.avif",
  defaultOgImage: "/img/og/default.png",
  sameAs: [
    "https://www.instagram.com/d10spizza/",
    "https://www.facebook.com/d10spizza/",
  ],
};

const toAbsUrl = (maybeRelative) => {
  if (!maybeRelative) return "";
  if (/^https?:\/\//i.test(maybeRelative)) return maybeRelative;
  const base = SITE.baseUrl.replace(/\/$/, "");
  const path = String(maybeRelative).startsWith("/") ? maybeRelative : `/${maybeRelative}`;
  return `${base}${path}`;
};

const stripHtml = (s = "") => String(s).replace(/<[^>]*>/g, "").trim();

const safeMetaDescription = (article) => {
  const raw = article?.excerpt || article?.subtitle || article?.title || `${SITE.name} story`;
  const clean = stripHtml(raw).replace(/\s+/g, " ").trim();
  return clean.length > 160 ? `${clean.slice(0, 157)}…` : clean;
};

const toISODate = (d) => {
  if (!d) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(d)) return d;
  return null;
};

/** --------------------------------------------
 * Share helpers
 * -------------------------------------------- */
function buildShareUrls({ url, title }) {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(title);

  return {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
    x: `https://twitter.com/intent/tweet?url=${u}&text=${t}`,
    email: `mailto:?subject=${t}&body=${encodeURIComponent(`${title}\n\n${url}`)}`,
    copyText: url,
  };
}

function openPopup(href) {
  const w = 640;
  const h = 720;
  const y = window.top?.outerHeight ? (window.top.outerHeight - h) / 2 : 80;
  const x = window.top?.outerWidth ? (window.top.outerWidth - w) / 2 : 80;
  window.open(
    href,
    "share",
    `toolbar=0,status=0,width=${w},height=${h},top=${Math.max(0, y)},left=${Math.max(0, x)}`
  );
}

function useShareActions({ canonical, title }) {
  const [copied, setCopied] = useState(false);

  const shareNative = async () => {
    if (navigator?.share) {
      await navigator.share({ title, url: canonical });
      return true;
    }
    return false;
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(canonical);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
      return true;
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = canonical;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setCopied(true);
        setTimeout(() => setCopied(false), 1400);
        return true;
      } catch {
        return false;
      }
    }
  };

  const { facebook, x, email } = buildShareUrls({ url: canonical, title });

  const shareToFacebook = () => openPopup(facebook);
  const shareToX = () => openPopup(x);
  const shareToEmail = () => {
    window.location.href = email;
  };

  return {
    copied,
    shareNative,
    copyLink,
    shareToFacebook,
    shareToX,
    shareToEmail,
    facebookUrl: facebook,
    xUrl: x,
    emailUrl: email,
  };
}

/** --------------------------------------------
 * Inline formatting with strong/italic + links
 * -------------------------------------------- */
function formatTextWithMarks(text, { strong = [], italic = [], links = [] } = {}) {
  let parts = [String(text ?? "")];
  let keyCounter = 0;
  const nextKey = (prefix) => `${prefix}-${keyCounter++}`;

  const applyMark = (needle, wrapFn) => {
    if (!needle) return;
    const next = [];

    parts.forEach((p) => {
      if (typeof p !== "string") {
        next.push(p);
        return;
      }
      const split = p.split(needle);
      split.forEach((chunk, idx) => {
        if (chunk) next.push(chunk);
        if (idx < split.length - 1) next.push(wrapFn(needle, nextKey));
      });
    });

    parts = next;
  };

  // Links first (longest first)
  [...links]
    .filter((l) => l && l.text && l.href)
    .sort((a, b) => (b.text?.length || 0) - (a.text?.length || 0))
    .forEach((l) => {
      applyMark(l.text, (v, keyFn) => {
        const isExternal =
          l.external ?? (typeof l.href === "string" && /^https?:\/\//i.test(l.href));
        return (
          <a
            key={keyFn(`a-${v}`)}
            href={l.href}
            target={isExternal ? "_blank" : undefined}
            rel={isExternal ? "noopener noreferrer" : undefined}
            className="text-[#0087E6] font-bold underline underline-offset-4 hover:opacity-80 transition-opacity"
            title={l.title || v}
          >
            {v}
          </a>
        );
      });
    });

  [...strong]
    .filter(Boolean)
    .sort((a, b) => (b.length || 0) - (a.length || 0))
    .forEach((s) => applyMark(s, (v, keyFn) => <strong key={keyFn(`s-${v}`)}>{v}</strong>));

  [...italic]
    .filter(Boolean)
    .sort((a, b) => (b.length || 0) - (a.length || 0))
    .forEach((i) => applyMark(i, (v, keyFn) => <em key={keyFn(`i-${v}`)}>{v}</em>));

  return parts.map((p, idx) =>
    typeof p === "string" ? <React.Fragment key={`t-${idx}`}>{p}</React.Fragment> : p
  );
}

const ArticleHero = ({ article }) => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <header
      ref={containerRef}
      className="relative w-full h-[100svh] bg-[#0F172A] text-white overflow-hidden flex items-center justify-center"
    >
      {/* Parallax background image */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 z-0">
        <img
          src={article.heroImage || article.image}
          alt={article.title}
          className="w-full h-full object-cover"
          fetchpriority="high"
        />
      </motion.div>

      {/* Overlay: radial vignette + tint for text legibility */}
      <div className="absolute inset-0 bg-black/40 z-[1]" />
      <div
        className="absolute inset-0 z-[1]"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 0%, rgba(15,23,42,0.5) 100%)",
        }}
      />
      <div className="absolute inset-0 bg-[#0087E6]/8 mix-blend-overlay z-[1]" />

      {/* ── Full-screen centered content ── */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 w-full h-full flex flex-col items-center justify-center px-5 sm:px-8 md:px-12 lg:px-16 text-center"
      >
        {/* Category badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="inline-flex items-center gap-1.5 sm:gap-2 bg-[#0087E6] text-white px-3 sm:px-4 py-1.5 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-5 sm:mb-8 md:mb-10"
        >
          <Star size={10} className="fill-white sm:w-3 sm:h-3" />
          {article.category}
        </motion.div>

        {/* Title — big, centered, fills the screen */}
        <motion.h1
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.3 }}
          className="font-anton uppercase max-w-[95%] sm:max-w-5xl md:max-w-6xl"
          style={{
            fontSize: "clamp(2rem, 8vw, 7rem)",
            lineHeight: 1,
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {article.title}
        </motion.h1>

        {/* Subtitle */}
        {article.subtitle && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="font-serif italic text-sm sm:text-lg md:text-xl lg:text-2xl xl:text-3xl text-white/80 max-w-[90%] sm:max-w-xl md:max-w-2xl lg:max-w-3xl leading-relaxed mt-5 sm:mt-8 md:mt-10"
          >
            "{article.subtitle}"
          </motion.p>
        )}
      </motion.div>

      {/* Scroll indicator pinned to bottom */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-5 sm:bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/40 animate-bounce"
        aria-hidden="true"
      >
        <ArrowDown className="w-5 h-5 sm:w-6 sm:h-6" />
      </motion.div>
    </header>
  );
};

/** --------------------------------------------
 * NEW: List block renderer
 * -------------------------------------------- */
const RenderList = ({ block }) => {
  const items = Array.isArray(block.items) ? block.items : [];
  const ordered = Boolean(block.ordered);
  const tone = block.tone || "card";
  const ListTag = ordered ? "ol" : "ul";

  const containerClass =
    tone === "plain"
      ? "my-2"
      : "my-2 sm:my-3 md:my-4 bg-[#F0F9FF] border border-blue-100 rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6";

  const listClass =
    (ordered ? "list-decimal" : "list-disc") +
    " pl-5 sm:pl-6 space-y-2 sm:space-y-2.5 md:space-y-3 text-base sm:text-lg md:text-xl leading-relaxed text-gray-700";

  return (
    <div className={containerClass}>
      {block.title ? (
        <div className="font-anton text-[#0F172A] uppercase text-sm sm:text-base mb-3">
          {block.title}
        </div>
      ) : null}

      <ListTag className={listClass}>
        {items.map((it, i) => {
          const itemObj = typeof it === "string" ? { text: it } : (it || {});
          const text = String(itemObj.text ?? "");

          return (
            <li key={i} className="marker:text-[#0087E6]">
              <span className="inline-flex gap-2">
                {block.icon === "check" ? (
                  <span className="mt-1 text-[#0087E6]">
                    <Check size={16} className="sm:w-[18px] sm:h-[18px]" />
                  </span>
                ) : null}
                <span>
                  {formatTextWithMarks(text, {
                    strong: itemObj.strong,
                    italic: itemObj.italic,
                    links: itemObj.links,
                  })}
                </span>
              </span>
            </li>
          );
        })}
      </ListTag>
    </div>
  );
};

const RenderBody = ({ blocks }) => {
  return (
    <div className="space-y-5 sm:space-y-6 md:space-y-8">
      {blocks.map((b, idx) => {
        if (b.type === "heading") {
          return (
            <h2 key={idx} className="font-anton text-xl sm:text-2xl md:text-3xl text-[#0F172A] uppercase pt-2 sm:pt-4 break-words">
              {b.text}
            </h2>
          );
        }

        if (b.type === "quote") {
          return (
            <figure key={idx} className="my-6 sm:my-8 md:my-12 py-5 sm:py-6 md:py-8 border-y border-gray-100 relative">
              <Quote className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2 text-[#0087E6] w-5 h-5 sm:w-6 sm:h-6" />
              <blockquote className="font-anton text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl text-[#0F172A] text-center uppercase leading-tight px-2 sm:px-4 break-words">
                "{b.text}"
              </blockquote>
            </figure>
          );
        }

        if (b.type === "image") {
          const width = b.width || 1600;
          const height = b.height || 1000;

          return (
            <figure key={idx} className="my-6 sm:my-8 md:my-12 -mx-4 sm:mx-0">
              <div className="sm:rounded-xl md:rounded-2xl overflow-hidden shadow-md sm:shadow-lg md:shadow-2xl">
                <img src={b.src} alt={b.alt || "D10 Pizza Fantasista"} className="w-full h-auto" loading="lazy" width={width} height={height} />
              </div>
              {b.caption && (
                <figcaption className="text-center text-[10px] sm:text-xs font-bold uppercase tracking-widest text-gray-400 mt-2 sm:mt-3 md:mt-4 px-4 sm:px-0">
                  {b.caption}
                </figcaption>
              )}
            </figure>
          );
        }

        if (b.type === "list") {
          return <RenderList key={idx} block={b} />;
        }

        if (b.type === "paragraph") {
          const raw = String(b.text || "");
          const trimmed = raw.trim();

          return (
            <p key={idx} className="text-base sm:text-lg md:text-xl leading-relaxed">
              {b.dropcap ? (
                <span className="float-left text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-anton text-[#0087E6] leading-[0.8] mr-2 sm:mr-3 mt-0.5 sm:mt-1">
                  {trimmed.charAt(0)}
                </span>
              ) : null}
              {b.dropcap
                ? formatTextWithMarks(trimmed.slice(1), { strong: b.strong, italic: b.italic, links: b.links })
                : formatTextWithMarks(raw, { strong: b.strong, italic: b.italic, links: b.links })}
            </p>
          );
        }

        return null;
      })}
    </div>
  );
};

const ArticleContent = ({ article, canonical }) => {
  const blocks = article.body || [];
  const share = useShareActions({ canonical, title: article.title });

  return (
    <section className="bg-white relative">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 relative -top-6 sm:-top-8 md:-top-10 z-20">
        <div className="bg-white rounded-xl sm:rounded-2xl md:rounded-3xl shadow-lg sm:shadow-xl p-4 sm:p-5 md:p-6 lg:p-8 max-w-4xl mx-auto border border-blue-50">
          <div className="flex flex-col gap-4 sm:gap-5 md:gap-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2.5 sm:gap-3 md:gap-4">
                <img
                  src={article.author_image || SITE.logo}
                  className="w-10 h-10 sm:w-11 sm:h-11 md:w-14 md:h-14 rounded-full object-cover border-2 border-[#0087E6] flex-shrink-0"
                  alt={article.author || SITE.name}
                  loading="lazy"
                />
                <div className="min-w-0">
                  <div className="font-anton text-[#0F172A] text-sm sm:text-base md:text-lg uppercase truncate">
                    {article.author}
                  </div>
                  <div className="text-[10px] sm:text-xs font-bold text-[#0087E6] uppercase tracking-widest truncate">
                    {article.role}
                  </div>
                </div>
              </div>

              <div className="flex gap-1.5 sm:gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={async () => {
                    const ok = await share.shareNative();
                    if (!ok) await share.copyLink();
                  }}
                  className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-full bg-[#F0F9FF] flex items-center justify-center text-[#0087E6] hover:bg-[#0087E6] hover:text-white transition-colors active:scale-95"
                  aria-label="Share this article"
                  title="Share"
                >
                  <Share2 size={14} className="sm:w-4 sm:h-4 md:w-[18px] md:h-[18px]" />
                </button>

                <button
                  type="button"
                  onClick={share.copyLink}
                  className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-full bg-[#F0F9FF] flex items-center justify-center text-[#0087E6] hover:bg-[#0087E6] hover:text-white transition-colors active:scale-95"
                  aria-label="Copy article link"
                  title="Copy link"
                >
                  <Bookmark size={14} className="sm:w-4 sm:h-4 md:w-[18px] md:h-[18px]" />
                </button>
              </div>
            </div>

            {share.copied && (
              <div className="text-center text-[10px] sm:text-xs font-bold uppercase tracking-widest text-[#0087E6]">
                Link copied ✓
              </div>
            )}

            <div className="h-px w-full bg-gray-100" />

            <div className="flex flex-wrap gap-3 sm:gap-4 md:gap-8 text-[#0F172A]/60 text-[10px] sm:text-xs font-bold uppercase tracking-widest justify-center sm:justify-start">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Calendar size={12} className="text-[#0087E6] sm:w-3.5 sm:h-3.5 md:w-4 md:h-4" />
                <span className="whitespace-nowrap">{article.date}</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Clock size={12} className="text-[#0087E6] sm:w-3.5 sm:h-3.5 md:w-4 md:h-4" />
                <span className="whitespace-nowrap">{article.readTime}</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 justify-center sm:justify-start pt-1">
              <button
                type="button"
                onClick={share.shareToFacebook}
                className="px-3 py-2 rounded-full bg-gray-50 text-gray-600 font-bold uppercase tracking-widest text-[10px] hover:bg-[#0087E6] hover:text-white transition-colors"
              >
                Facebook
              </button>
              <button
                type="button"
                onClick={share.shareToX}
                className="px-3 py-2 rounded-full bg-gray-50 text-gray-600 font-bold uppercase tracking-widest text-[10px] hover:bg-[#0087E6] hover:text-white transition-colors"
              >
                X
              </button>
              <button
                type="button"
                onClick={share.shareToEmail}
                className="px-3 py-2 rounded-full bg-gray-50 text-gray-600 font-bold uppercase tracking-widest text-[10px] hover:bg-[#0087E6] hover:text-white transition-colors"
              >
                Email
              </button>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 sm:gap-8 md:gap-10 lg:gap-12 mt-8 sm:mt-12 md:mt-16 lg:mt-20 xl:mt-24 max-w-7xl mx-auto">
          <aside className="hidden lg:block lg:col-span-2" aria-label="Share">
            <div className="sticky top-28 xl:top-32 flex flex-col items-center gap-4 xl:gap-6">
              <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 rotate-180" style={{ writingMode: "vertical-rl" }}>
                Share this Story
              </div>
              <div className="w-px h-8 xl:h-12 bg-gray-200" />

              <button
                type="button"
                onClick={share.shareToFacebook}
                className="text-gray-400 hover:text-[#0087E6] transition-colors p-1"
                aria-label="Share on Facebook"
                title="Share on Facebook"
              >
                <Facebook size={18} className="xl:w-5 xl:h-5" />
              </button>

              <button
                type="button"
                onClick={share.shareToX}
                className="text-gray-400 hover:text-[#0087E6] transition-colors p-1"
                aria-label="Share on X"
                title="Share on X"
              >
                <ArrowRight size={18} className="xl:w-5 xl:h-5 rotate-45" />
              </button>

              <button
                type="button"
                onClick={share.shareToEmail}
                className="text-gray-400 hover:text-[#0087E6] transition-colors p-1"
                aria-label="Share by Email"
                title="Share by Email"
              >
                <Mail size={18} className="xl:w-5 xl:h-5" />
              </button>

              <button
                type="button"
                onClick={share.copyLink}
                className="text-gray-400 hover:text-[#0087E6] transition-colors p-1"
                aria-label="Copy link"
                title="Copy link"
              >
                <Bookmark size={18} className="xl:w-5 xl:h-5" />
              </button>

              <a
                href={SITE.sameAs?.[0] || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#0087E6] transition-colors p-1"
                aria-label="Open Instagram"
                title="Open Instagram"
              >
                <Instagram size={18} className="xl:w-5 xl:h-5" />
              </a>
            </div>
          </aside>

          <main className="lg:col-span-8">
            <article className="font-manrope text-gray-600 leading-relaxed">
              {blocks.length ? (
                <RenderBody blocks={blocks} />
              ) : (
                <p className="text-base sm:text-lg md:text-xl">
                  No content yet. Add <code className="text-[#0087E6]">body: []</code> blocks to this article in your data.
                </p>
              )}
            </article>
          </main>

          <aside className="hidden lg:block lg:col-span-2" aria-label="Key stats">
            <div className="sticky top-28 xl:top-32">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#0087E6] mb-3 xl:mb-4">
                Key Stats
              </div>
              <div className="space-y-4 xl:space-y-6">
                {(article.keyStats || []).map((s, i) => (
                  <div key={i} className="bg-[#F0F9FF] p-3 xl:p-4 rounded-lg xl:rounded-xl">
                    <div className="text-xl xl:text-2xl font-anton text-[#0F172A]">{s.value}</div>
                    <div className="text-[9px] xl:text-[10px] uppercase text-gray-500 font-bold">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>

        {/* Mobile share row */}
        <div className="lg:hidden max-w-4xl mx-auto mt-6 sm:mt-8 md:mt-10 pt-5 sm:pt-6 md:pt-8 border-t border-gray-100">
          <div className="flex items-center justify-center gap-3 sm:gap-4">
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-gray-400">
              Share this Story
            </span>
            <div className="flex gap-2 sm:gap-3">
              <button
                type="button"
                onClick={async () => {
                  const ok = await share.shareNative();
                  if (!ok) await share.copyLink();
                }}
                className="w-9 h-9 rounded-full bg-[#F0F9FF] flex items-center justify-center text-[#0087E6] hover:bg-[#0087E6] hover:text-white transition-colors active:scale-95"
                aria-label="Share"
              >
                <Share2 size={16} />
              </button>
              <button
                type="button"
                onClick={share.shareToFacebook}
                className="w-9 h-9 rounded-full bg-[#F0F9FF] flex items-center justify-center text-[#0087E6] hover:bg-[#0087E6] hover:text-white transition-colors active:scale-95"
                aria-label="Share on Facebook"
              >
                <Facebook size={16} />
              </button>
              <button
                type="button"
                onClick={share.shareToEmail}
                className="w-9 h-9 rounded-full bg-[#F0F9FF] flex items-center justify-center text-[#0087E6] hover:bg-[#0087E6] hover:text-white transition-colors active:scale-95"
                aria-label="Share by email"
              >
                <Mail size={16} />
              </button>
              <button
                type="button"
                onClick={share.copyLink}
                className="w-9 h-9 rounded-full bg-[#F0F9FF] flex items-center justify-center text-[#0087E6] hover:bg-[#0087E6] hover:text-white transition-colors active:scale-95"
                aria-label="Copy link"
              >
                <Bookmark size={16} />
              </button>
            </div>
          </div>
          {share.copied && (
            <div className="text-center text-[10px] sm:text-xs font-bold uppercase tracking-widest text-[#0087E6] mt-3">
              Link copied ✓
            </div>
          )}
        </div>

        {/* Tags */}
        <div className="max-w-4xl mx-auto mt-6 sm:mt-8 md:mt-12 lg:mt-16 pt-5 sm:pt-6 md:pt-8 border-t border-gray-100 flex flex-wrap gap-1.5 sm:gap-2 justify-center lg:justify-start">
          {(article.tags || []).map((tag) => (
            <span key={tag} className="px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 bg-gray-50 text-gray-500 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-widest">
              #{tag}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

const ReadNext = ({ next }) => {
  if (!next) return null;

  const getTitleClasses = (title) => {
    const len = title?.length || 0;
    if (len > 50) return "text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl";
    return "text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl";
  };

  return (
    <Link to={`/blog/${next.slug}`} className="block" aria-label={`Read next: ${next.title}`}>
      <section className="bg-[#0F172A] py-10 sm:py-12 md:py-16 lg:py-20 mt-10 sm:mt-12 md:mt-16 lg:mt-20 relative overflow-hidden group cursor-pointer">
        <div className="absolute inset-0 opacity-20 transition-opacity duration-700 group-hover:opacity-30">
          <img src={next.image || next.heroImage} alt={next.title} className="w-full h-full object-cover grayscale" loading="lazy" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/80 to-transparent" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 relative z-10">
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4 text-[#0087E6] mb-2.5 sm:mb-3 md:mb-4">
            <Calendar size={12} className="sm:w-3.5 sm:h-3.5 md:w-4 md:h-4" />
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest">Read Next</span>
          </div>

          <div className="max-w-3xl">
            <h2 className={`font-anton ${getTitleClasses(next.title)} text-white uppercase mb-3 sm:mb-4 md:mb-6 group-hover:text-[#0087E6] transition-colors leading-[0.9] break-words`}>
              {next.title}
            </h2>

            {next.excerpt && (
              <p className="font-serif italic text-white/60 text-sm sm:text-base md:text-lg lg:text-xl mb-4 sm:mb-5 md:mb-6 lg:mb-8 line-clamp-2 md:line-clamp-3 lg:line-clamp-none">
                {next.excerpt}
              </p>
            )}

            <div className="inline-flex items-center gap-2 text-white font-bold uppercase tracking-widest text-[11px] sm:text-xs md:text-sm group-hover:gap-3 md:group-hover:gap-4 transition-all">
              Read Article <ArrowRight size={12} className="text-[#0087E6] sm:w-3.5 sm:h-3.5 md:w-4 md:h-4" />
            </div>
          </div>
        </div>
      </section>
    </Link>
  );
};

export default function ArticlePage() {
  const { slug } = useParams();
  const article = useMemo(() => getArticleBySlug(slug), [slug]);
  const next = useMemo(() => getNextArticle(slug), [slug]);

  if (!article) {
    const notFoundCanonical = `${SITE.baseUrl}/blog/${slug || ""}`.replace(/\/+$/, "");
    return (
      <div className="min-h-screen bg-white">
        <Helmet>
          <title>Article not found | {SITE.name}</title>
          <meta name="robots" content="noindex,follow" />
          <link rel="canonical" href={notFoundCanonical} />
        </Helmet>
        <Navbar />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 md:py-28 lg:py-32">
          <h1 className="font-anton text-2xl sm:text-3xl md:text-4xl uppercase text-[#0F172A] mb-3 sm:mb-4">Article not found</h1>
          <Link to="/blog" className="text-[#0087E6] font-bold uppercase tracking-widest text-xs sm:text-sm inline-flex items-center gap-2 hover:gap-3 transition-all">
            <ArrowRight size={14} className="rotate-180" /> Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const canonical = `${SITE.baseUrl}/blog/${article.slug}`;
  const metaDescription = safeMetaDescription(article);
  const ogImageAbs = toAbsUrl(article.heroImage || article.image || SITE.defaultOgImage);

  const datePublished = toISODate(article.datePublished) || toISODate(article.dateModified) || null;
  const dateModified = toISODate(article.dateModified) || datePublished || null;

  const jsonLdArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
    headline: article.title,
    description: metaDescription,
    image: ogImageAbs ? [ogImageAbs] : undefined,
    author: article.author ? { "@type": "Person", name: article.author } : { "@type": "Organization", name: SITE.name },
    publisher: {
      "@type": "Organization",
      name: SITE.name,
      logo: SITE.logo ? { "@type": "ImageObject", url: toAbsUrl(SITE.logo) } : undefined,
      sameAs: SITE.sameAs,
    },
    datePublished: datePublished || undefined,
    dateModified: dateModified || undefined,
    keywords: (article.tags || []).join(", ") || undefined,
  };

  const jsonLdBreadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.baseUrl}/` },
      { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE.baseUrl}/blog` },
      { "@type": "ListItem", position: 3, name: article.title, item: canonical },
    ],
  };

  return (
    <div className="bg-white min-h-screen w-full overflow-x-hidden selection:bg-[#0087E6] selection:text-white">
      <Helmet>
        <title>
          {article.title} | {SITE.name}
        </title>
        <link rel="canonical" href={canonical} />
        <meta name="description" content={metaDescription} />
        <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />

        <meta property="og:site_name" content={SITE.name} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        {ogImageAbs && <meta property="og:image" content={ogImageAbs} />}
        {ogImageAbs && <meta property="og:image:alt" content={article.title} />}

        {datePublished && <meta property="article:published_time" content={datePublished} />}
        {dateModified && <meta property="article:modified_time" content={dateModified} />}
        {(article.tags || []).slice(0, 8).map((t) => (
          <meta key={t} property="article:tag" content={t} />
        ))}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={metaDescription} />
        {ogImageAbs && <meta name="twitter:image" content={ogImageAbs} />}

        {ogImageAbs && <link rel="preload" as="image" href={ogImageAbs} />}

        <script type="application/ld+json">{JSON.stringify(jsonLdBreadcrumbs)}</script>
        <script type="application/ld+json">{JSON.stringify(jsonLdArticle)}</script>
      </Helmet>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Anton&family=Manrope:wght@400;500;700;800&family=Playfair+Display:ital,wght@0,400;1,400;1,700&display=swap');
        .font-anton { font-family: 'Anton', sans-serif; }
        .font-manrope { font-family: 'Manrope', sans-serif; }
        .font-serif { font-family: 'Playfair Display', serif; }
        html { scroll-behavior: smooth; }
        @supports (height: 100svh) { .min-h-\\[100svh\\] { min-height: 100svh; } }
        @supports not (height: 100svh) { .min-h-\\[100svh\\] { min-height: 100vh; } }
        @media (min-width: 768px) {
          ::-webkit-scrollbar { width: 8px; }
          ::-webkit-scrollbar-track { background: #F0F9FF; }
          ::-webkit-scrollbar-thumb { background: #0087E6; border-radius: 4px; }
          ::-webkit-scrollbar-thumb:hover { background: #0F172A; }
        }
        @media (max-width: 767px) {
          ::-webkit-scrollbar { display: none; }
          html { -ms-overflow-style: none; scrollbar-width: none; }
        }
        @media (max-width: 640px) { button, a { min-height: 44px; } }
        html, body { overflow-x: hidden; max-width: 100vw; }
        .line-clamp-2 {
          display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
        }
        .line-clamp-3 {
          display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
        }
        .break-words { word-wrap: break-word; overflow-wrap: break-word; word-break: break-word; }
        @supports (padding: env(safe-area-inset-bottom)) {
          .container {
            padding-left: max(1rem, env(safe-area-inset-left));
            padding-right: max(1rem, env(safe-area-inset-right));
          }
        }
      `}</style>

      <Navbar />
      <ArticleHero article={article} />
      <ArticleContent article={article} canonical={canonical} />
      <ReadNext next={next} />
      <Footer />
    </div>
  );
}